import React, { useState, useEffect } from 'react';
import {
  Box, Plus, Edit2, Trash2, Search, Package,
  TrendingUp, TrendingDown, X, History, Edit3, Check
} from 'lucide-react';
import api, { API_URL } from '../../services/api';

const MOTIFS_ENTREE = [
  { value: 'ACHAT', label: 'Achat fournisseur' },
  { value: 'RETOUR_PRODUCTION', label: 'Retour production' },
  { value: 'INVENTAIRE', label: 'Ajustement inventaire' },
];
const MOTIFS_SORTIE = [
  { value: 'UTILISATION_PRODUCTION', label: 'Utilisation production' },
  { value: 'UTILISATION_DEVIS', label: 'Utilisation devis' },
  { value: 'PERTE', label: 'Perte / casse' },
  { value: 'INVENTAIRE', label: 'Ajustement inventaire' },
];

function StockComposants() {
  const [composants, setComposants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredComposants, setFilteredComposants] = useState([]);
  const [selectedCategorie, setSelectedCategorie] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingComposant, setEditingComposant] = useState(null);
  const [showMouvementModal, setShowMouvementModal] = useState(false);
  const [selectedComposant, setSelectedComposant] = useState(null);
  const [showLotsModal, setShowLotsModal] = useState(false);
  const [lotsData, setLotsData] = useState(null);
  const [editingPrixMarche, setEditingPrixMarche] = useState(false);
  const [prixMarcheInput, setPrixMarcheInput] = useState('');

  const [formData, setFormData] = useState({
    code_composant: '', reference: '', nom: '', categorie_id: '',
    description: '', unite_mesure: 'piece',
    prix_achat_unitaire: '', stock_actuel: '', stock_minimum: '',
    emplacement_stockage: '', statut: 'actif', notes: ''
  });
  const [formMouvement, setFormMouvement] = useState({
    type_mouvement: 'ENTREE', quantite: '', motif: 'ACHAT',
    prix_unitaire: '', notes: ''
  });

  useEffect(() => { loadData(); }, []);
  useEffect(() => { filterComposants(); }, [composants, selectedCategorie, searchTerm]);

  const loadData = async () => {
    try {
      const [composantsRes, categoriesRes] = await Promise.all([
        api.get(`${API_URL}/composants`),
        api.get(`${API_URL}/composants/categories`)
      ]);
      setComposants(composantsRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    }
  };

  const filterComposants = () => {
    let filtered = composants;
    if (selectedCategorie) {
      filtered = filtered.filter(c => c.categorie_id === parseInt(selectedCategorie));
    }
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.code_composant?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.reference?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredComposants(filtered);
  };

  const handleOpenModal = (composant = null) => {
    if (composant) {
      setEditingComposant(composant);
      setFormData({
        code_composant: composant.code_composant || '',
        reference: composant.reference || '',
        nom: composant.nom || '',
        categorie_id: composant.categorie_id || '',
        description: composant.description || '',
        unite_mesure: composant.unite_mesure || 'piece',
        prix_achat_unitaire: composant.prix_achat_unitaire || '',
        stock_actuel: composant.stock_actuel || '',
        stock_minimum: composant.stock_minimum || '',
        emplacement_stockage: composant.emplacement_stockage || '',
        statut: composant.statut || 'actif',
        notes: composant.notes || ''
      });
    } else {
      setEditingComposant(null);
      setFormData({
        code_composant: '', reference: '', nom: '', categorie_id: '',
        description: '', unite_mesure: 'piece',
        prix_achat_unitaire: '', stock_actuel: '', stock_minimum: '',
        emplacement_stockage: '', statut: 'actif', notes: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingComposant) {
        await api.put(`${API_URL}/composants/${editingComposant.id}`, formData);
      } else {
        await api.post(`${API_URL}/composants`, formData);
      }
      setShowModal(false);
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce composant ?')) return;
    try {
      await api.delete(`${API_URL}/composants/${id}`);
      await loadData();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleOpenMouvement = (composant) => {
    setSelectedComposant(composant);
    setFormMouvement({
      type_mouvement: 'ENTREE', quantite: '', motif: 'ACHAT',
      prix_unitaire: composant.prix_achat_unitaire || '', notes: ''
    });
    setShowMouvementModal(true);
  };

  const handleSaveMouvement = async () => {
    try {
      await api.post(`${API_URL}/composants/${selectedComposant.id}/mouvements`, formMouvement);
      setShowMouvementModal(false);
      await loadData();
      // Refresh lots if open
      if (showLotsModal && lotsData) {
        await loadLots(selectedComposant.id);
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Erreur lors de l\'enregistrement';
      alert(msg);
    }
  };

  const loadLots = async (composantId) => {
    try {
      const res = await api.get(`${API_URL}/composants/${composantId}/lots`);
      setLotsData(res.data);
      setPrixMarcheInput(res.data.prix_marche_actuel || '');
    } catch (error) {
      console.error('Erreur chargement lots:', error);
    }
  };

  const handleOpenLots = async (composant) => {
    setSelectedComposant(composant);
    await loadLots(composant.id);
    setEditingPrixMarche(false);
    setShowLotsModal(true);
  };

  const handleSavePrixMarche = async () => {
    try {
      await api.put(`${API_URL}/composants/${selectedComposant.id}/prix-marche`, {
        prix_marche_actuel: parseFloat(prixMarcheInput)
      });
      setEditingPrixMarche(false);
      await loadLots(selectedComposant.id);
      await loadData();
    } catch (error) {
      alert('Erreur mise à jour prix marché');
    }
  };

  const getStockColor = (stock, minimum) => {
    if (stock <= 0) return 'text-red-600 font-bold';
    if (stock <= minimum) return 'text-orange-600 font-bold';
    return 'text-green-600 font-bold';
  };

  const fmt = (n, dec = 2) => parseFloat(n || 0).toLocaleString('fr-MA', { minimumFractionDigits: dec, maximumFractionDigits: dec });

  const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-MA', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const currentMotifs = formMouvement.type_mouvement === 'ENTREE' ? MOTIFS_ENTREE : MOTIFS_SORTIE;

  return (
    <div className="space-y-6">
      {/* Filtres */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input type="text" value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un composant..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <select value={selectedCategorie} onChange={(e) => setSelectedCategorie(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
            <option value="">Toutes les catégories</option>
            {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.nom}</option>)}
          </select>
          <button onClick={() => handleOpenModal()}
            className="w-full sm:w-auto px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg flex items-center justify-center gap-2 font-semibold">
            <Plus className="h-5 w-5" />
            Nouveau
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: composants.length, color: 'text-blue-600' },
          { label: 'En stock', value: composants.filter(c => c.stock_actuel > 0).length, color: 'text-green-600' },
          { label: 'Stock faible', value: composants.filter(c => c.stock_actuel > 0 && c.stock_actuel <= c.stock_minimum).length, color: 'text-orange-600' },
          { label: 'Rupture', value: composants.filter(c => c.stock_actuel <= 0).length, color: 'text-red-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600 mb-1">{s.label}</div>
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {filteredComposants.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            <Box className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>Aucun composant trouvé</p>
          </div>
        ) : filteredComposants.map(comp => (
          <div key={comp.id} className={`bg-white rounded-lg shadow p-4 border-l-4 ${comp.stock_actuel <= 0 ? 'border-red-400' : comp.stock_actuel <= comp.stock_minimum ? 'border-orange-400' : 'border-blue-400'}`}>
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900 truncate">{comp.nom}</div>
                <div className="text-xs text-gray-500">{comp.code_composant} · {comp.categorie_nom}</div>
              </div>
              <span className={`text-sm font-bold ml-2 ${getStockColor(comp.stock_actuel, comp.stock_minimum)}`}>
                {comp.stock_actuel} {comp.unite_mesure}
              </span>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mb-3">
              <span>Prix: {comp.prix_achat_unitaire ? `${comp.prix_achat_unitaire} DH` : '—'}</span>
              {comp.prix_marche_actuel && <span className="font-semibold text-amber-700">Marché: {comp.prix_marche_actuel} DH</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleOpenLots(comp)}
                className="flex-1 py-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg flex items-center justify-center gap-1">
                <History className="h-3.5 w-3.5" /> Lots
              </button>
              <button onClick={() => handleOpenMouvement(comp)}
                className="flex-1 py-1.5 text-xs bg-green-50 text-green-700 border border-green-200 rounded-lg flex items-center justify-center gap-1">
                <Package className="h-3.5 w-3.5" /> Stock
              </button>
              <button onClick={() => handleOpenModal(comp)}
                className="flex-1 py-1.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-lg flex items-center justify-center gap-1">
                <Edit2 className="h-3.5 w-3.5" /> Modifier
              </button>
              <button onClick={() => handleDelete(comp.id)}
                className="py-1.5 px-2 text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unité</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix moy.</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix marché</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredComposants.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                  <Box className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Aucun composant trouvé</p>
                </td>
              </tr>
            ) : filteredComposants.map(comp => (
              <tr key={comp.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-mono text-gray-700">{comp.code_composant}</td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{comp.nom}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{comp.categorie_nom}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={getStockColor(comp.stock_actuel, comp.stock_minimum)}>
                    {fmt(comp.stock_actuel)}
                  </span>
                  {comp.stock_minimum > 0 && (
                    <span className="text-xs text-gray-400 ml-1">(min: {comp.stock_minimum})</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 capitalize">{comp.unite_mesure}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{fmt(comp.prix_achat_unitaire)} DH</td>
                <td className="px-4 py-3 text-sm">
                  {comp.prix_marche_actuel ? (
                    <span className="font-semibold text-amber-700">{fmt(comp.prix_marche_actuel)} DH</span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex gap-1">
                    <button onClick={() => handleOpenLots(comp)}
                      className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="Historique lots">
                      <History className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleOpenMouvement(comp)}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Mouvement stock">
                      <Package className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleOpenModal(comp)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Modifier">
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(comp.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Supprimer">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Modal Lots / Historique ── */}
      {showLotsModal && lotsData && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white rounded-t-2xl sm:rounded-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="flex justify-between items-center p-5 border-b"
              style={{ background: 'linear-gradient(135deg, #78350f, #b45309)' }}>
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <History className="h-5 w-5" />
                  {lotsData.nom} — Historique achats
                </h2>
                <p className="text-amber-200 text-sm mt-0.5">
                  Stock actuel : <strong>{fmt(lotsData.stock_actuel)} {lotsData.unite_mesure}</strong>
                </p>
              </div>
              <button onClick={() => setShowLotsModal(false)}
                className="p-2 hover:bg-white/20 rounded-full text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-5 overflow-y-auto flex-1">
              {/* Comparaison prix */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide mb-1">
                    Prix moyen pondéré (coût réel)
                  </div>
                  <div className="text-2xl font-bold text-amber-900">
                    {fmt(lotsData.prix_moyen_pondere)} DH
                  </div>
                  <div className="text-xs text-amber-600 mt-1">
                    Valeur stock : {fmt(lotsData.stock_actuel * lotsData.prix_moyen_pondere)} DH
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs text-blue-700 font-semibold uppercase tracking-wide">
                      Prix marché actuel (devis)
                    </div>
                    {!editingPrixMarche ? (
                      <button onClick={() => { setEditingPrixMarche(true); setPrixMarcheInput(lotsData.prix_marche_actuel || ''); }}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded">
                        <Edit3 className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button onClick={handleSavePrixMarche}
                        className="p-1 text-green-600 hover:bg-green-100 rounded">
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {editingPrixMarche ? (
                    <input type="text" inputMode="decimal" onFocus={e => e.target.select()} step="0.01" value={prixMarcheInput}
                      onChange={(e) => setPrixMarcheInput(e.target.value)}
                      className="w-full text-xl font-bold text-blue-900 border-b-2 border-blue-400 bg-transparent focus:outline-none"
                      autoFocus />
                  ) : (
                    <div className="text-2xl font-bold text-blue-900">
                      {lotsData.prix_marche_actuel ? `${fmt(lotsData.prix_marche_actuel)} DH` : <span className="text-gray-400 text-base">Non défini</span>}
                    </div>
                  )}
                  {lotsData.prix_marche_actuel && lotsData.prix_moyen_pondere > 0 && (
                    <div className={`text-xs mt-1 font-medium ${lotsData.prix_marche_actuel >= lotsData.prix_moyen_pondere ? 'text-green-600' : 'text-red-600'}`}>
                      {lotsData.prix_marche_actuel >= lotsData.prix_moyen_pondere ? '▲' : '▼'}
                      {' '}{fmt(Math.abs(lotsData.prix_marche_actuel - lotsData.prix_moyen_pondere))} DH vs coût
                    </div>
                  )}
                </div>
              </div>

              {/* Lots d'achat */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Lots d'achat (chronologique)
                </h3>
                {lotsData.lots.length === 0 ? (
                  <div className="text-center text-gray-400 py-8 bg-gray-50 rounded-lg">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Aucun achat enregistré</p>
                    <p className="text-xs">Utilisez "Mouvement stock" → Achat fournisseur</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {lotsData.lots.map((lot, i) => {
                      const valeur = parseFloat(lot.cout_total || (lot.quantite * lot.prix_unitaire) || 0);
                      return (
                        <div key={lot.id}
                          className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-100 hover:border-amber-200 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center justify-center flex-shrink-0">
                              {i + 1}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-800">
                                {fmt(lot.quantite, 0)} {lotsData.unite_mesure}
                                <span className="text-gray-400 mx-1">@</span>
                                <span className="text-amber-700">{fmt(lot.prix_unitaire)} DH/{lotsData.unite_mesure}</span>
                              </div>
                              {lot.notes && <div className="text-xs text-gray-400">{lot.notes}</div>}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-gray-800">{fmt(valeur)} DH</div>
                            <div className="text-xs text-gray-400">{fmtDate(lot.date_mouvement)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Bouton ajouter un lot */}
              <button
                onClick={() => {
                  setShowLotsModal(false);
                  setFormMouvement({ type_mouvement: 'ENTREE', quantite: '', motif: 'ACHAT', prix_unitaire: '', notes: '' });
                  setShowMouvementModal(true);
                }}
                className="w-full py-2.5 border-2 border-dashed border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors text-sm font-medium flex items-center justify-center gap-2">
                <Plus className="h-4 w-4" />
                Enregistrer un nouveau lot d'achat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Ajouter/Modifier Composant ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-5 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Box className="h-5 w-5" />
                {editingComposant ? 'Modifier Composant' : 'Nouveau Composant'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/20 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input type="text" value={formData.code_composant}
                    onChange={(e) => setFormData({ ...formData, code_composant: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Auto si vide" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
                  <input type="text" value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input type="text" value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Or, Fermoir magnétique..." />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                  <select value={formData.categorie_id}
                    onChange={(e) => setFormData({ ...formData, categorie_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="">Sélectionner...</option>
                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unité</label>
                  <select value={formData.unite_mesure}
                    onChange={(e) => setFormData({ ...formData, unite_mesure: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="piece">Pièce</option>
                    <option value="gramme">Gramme</option>
                    <option value="kilogramme">Kilogramme</option>
                    <option value="metre">Mètre</option>
                    <option value="lot">Lot</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="2" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock initial</label>
                  <input type="text" inputMode="decimal" onFocus={e => e.target.select()} step="0.01" value={formData.stock_actuel}
                    onChange={(e) => setFormData({ ...formData, stock_actuel: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock minimum</label>
                  <input type="text" inputMode="decimal" onFocus={e => e.target.select()} step="0.01" value={formData.stock_minimum}
                    onChange={(e) => setFormData({ ...formData, stock_minimum: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix achat moyen</label>
                  <input type="text" inputMode="decimal" onFocus={e => e.target.select()} step="0.01" value={formData.prix_achat_unitaire}
                    onChange={(e) => setFormData({ ...formData, prix_achat_unitaire: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emplacement</label>
                  <input type="text" value={formData.emplacement_stockage}
                    onChange={(e) => setFormData({ ...formData, emplacement_stockage: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Étagère A3" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select value={formData.statut}
                    onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                    <option value="rupture">Rupture</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="p-5 border-t bg-gray-50 flex flex-col sm:flex-row justify-end gap-3">
              <button onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium">
                Annuler
              </button>
              <button onClick={handleSave}
                className="w-full sm:w-auto px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium">
                {editingComposant ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Mouvement Stock ── */}
      {showMouvementModal && selectedComposant && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-md shadow-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b bg-gradient-to-r from-green-600 to-teal-600 text-white">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Package className="h-5 w-5" />
                Mouvement de stock
              </h2>
              <button onClick={() => setShowMouvementModal(false)} className="p-2 hover:bg-white/20 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="font-semibold text-gray-800">{selectedComposant.nom}</div>
                <div className="text-sm text-gray-500">
                  Stock actuel : {fmt(selectedComposant.stock_actuel)} {selectedComposant.unite_mesure}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {['ENTREE', 'SORTIE'].map(type => (
                  <button key={type}
                    onClick={() => {
                      const motifs = type === 'ENTREE' ? MOTIFS_ENTREE : MOTIFS_SORTIE;
                      setFormMouvement({ ...formMouvement, type_mouvement: type, motif: motifs[0].value });
                    }}
                    className={`p-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                      formMouvement.type_mouvement === type
                        ? type === 'ENTREE' ? 'bg-green-600 text-white shadow-md' : 'bg-red-600 text-white shadow-md'
                        : 'border-2 border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}>
                    {type === 'ENTREE' ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
                    {type === 'ENTREE' ? 'Entrée' : 'Sortie'}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motif *</label>
                <select value={formMouvement.motif}
                  onChange={(e) => setFormMouvement({ ...formMouvement, motif: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500">
                  {currentMotifs.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantité *</label>
                <input type="text" inputMode="decimal" onFocus={e => e.target.select()} step="0.01" value={formMouvement.quantite}
                  onChange={(e) => setFormMouvement({ ...formMouvement, quantite: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500" />
              </div>

              {formMouvement.type_mouvement === 'ENTREE' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prix unitaire (DH/{selectedComposant.unite_mesure})
                  </label>
                  <input type="text" inputMode="decimal" onFocus={e => e.target.select()} step="0.01" value={formMouvement.prix_unitaire}
                    onChange={(e) => setFormMouvement({ ...formMouvement, prix_unitaire: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500" />
                  {formMouvement.prix_unitaire && formMouvement.quantite && (
                    <div className="text-xs text-gray-500 mt-1">
                      Valeur lot : {fmt(parseFloat(formMouvement.prix_unitaire) * parseFloat(formMouvement.quantite))} DH
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formMouvement.notes}
                  onChange={(e) => setFormMouvement({ ...formMouvement, notes: e.target.value })}
                  rows="2" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500" />
              </div>
            </div>

            <div className="p-5 border-t bg-gray-50 flex flex-col sm:flex-row justify-end gap-3">
              <button onClick={() => setShowMouvementModal(false)}
                className="w-full sm:w-auto px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium">
                Annuler
              </button>
              <button onClick={handleSaveMouvement}
                className="w-full sm:w-auto px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StockComposants;
